import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Save, ArrowLeft, Building2, User, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SystemConfigPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    agency_name: '',
    agency_phone: '',
    agency_email: '',
    agency_website: '',
    agency_address: '',
    signature_text: '',
    footer_text: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/email-campaigns/config/system`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        setFormData({
          agency_name: response.data.agency_name || '',
          agency_phone: response.data.agency_phone || '',
          agency_email: response.data.agency_email || '',
          agency_website: response.data.agency_website || '',
          agency_address: response.data.agency_address || '',
          signature_text: response.data.signature_text || '',
          footer_text: response.data.footer_text || ''
        });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agency_name) {
      toast.error('El nombre de la agencia es requerido');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/email-campaigns/config/system`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/email-campaigns')}
          variant="ghost"
          className="mb-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Building2 className="h-8 w-8 text-indigo-600" />
          Configuración del Sistema
        </h1>
        <p className="text-gray-600 mt-2">
          Configura las variables del sistema que se usan en todas las campañas de email
        </p>
      </div>

      {/* Guía Completa de Variables */}
      <div className="mb-6 space-y-4">
        {/* Variables del Sistema */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Settings className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900 flex-1">
                <p className="font-semibold mb-3 text-base">⚙️ Variables del Sistema Disponibles:</p>
                <ul className="space-y-2 text-blue-800">
                  <li className="flex flex-col">
                    <code className="bg-blue-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{nombre_agencia}}'}</code>
                    <span className="text-xs">→ Nombre de tu agencia (configurable abajo)</span>
                  </li>
                  <li className="flex flex-col">
                    <code className="bg-blue-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{telefono_agencia}}'}</code>
                    <span className="text-xs">→ Teléfono de contacto (configurable abajo)</span>
                  </li>
                  <li className="flex flex-col">
                    <code className="bg-blue-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{email_agencia}}'}</code>
                    <span className="text-xs">→ Email de contacto (configurable abajo)</span>
                  </li>
                  <li className="flex flex-col">
                    <code className="bg-blue-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{sitio_web_agencia}}'}</code>
                    <span className="text-xs">→ Sitio web (configurable abajo)</span>
                  </li>
                  <li className="flex flex-col">
                    <code className="bg-blue-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{fecha_actual}}'}</code>
                    <span className="text-xs">→ Fecha del día (automática: DD/MM/YYYY)</span>
                  </li>
                </ul>
                <p className="mt-3 text-blue-700 font-medium">
                  ✨ Estas variables se pueden usar en todas tus campañas de email
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Variables de Cliente */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-900 flex-1">
                <p className="font-semibold mb-3 text-base">👤 Variables de Cliente (campos de la base de datos):</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <code className="bg-green-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{nombre}}'}</code>
                      <span className="text-xs text-green-800">→ Client.name</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-green-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{email}}'}</code>
                      <span className="text-xs text-green-800">→ Client.email</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-green-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{telefono}}'}</code>
                      <span className="text-xs text-green-800">→ Client.phone</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-green-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{alias}}'}</code>
                      <span className="text-xs text-green-800">→ Client.alias</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <code className="bg-green-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{rfc}}'}</code>
                      <span className="text-xs text-green-800">→ Client.id_number</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-green-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{ciudad}}'}</code>
                      <span className="text-xs text-green-800">→ Client.current_address.city</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-green-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{estado}}'}</code>
                      <span className="text-xs text-green-800">→ Client.current_address.state</span>
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-green-700 font-medium text-xs">
                  ✅ Variables validadas con el modelo Client de la base de datos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Variables de Póliza */}
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-900 flex-1">
                <p className="font-semibold mb-3 text-base">📋 Variables de Póliza (campos de la base de datos):</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{numero_poliza}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.policy_number</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{aseguradora}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.insurer</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{ramo}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.policy_type</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{plan}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.plan</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{fecha_inicio}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.start_date</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{fecha_vencimiento}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.end_date</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{prima_anual}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.annual_premium</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{suma_asegurada}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.insured_amount</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{contratante}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.contractor.name</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{asegurado_principal}}'}</code>
                      <span className="text-xs text-purple-800">→ Policy.main_insured.name</span>
                    </div>
                    <div className="flex flex-col">
                      <code className="bg-purple-100 px-2 py-1 rounded font-mono text-xs w-fit mb-1">{'{{dias_para_vencimiento}}'}</code>
                      <span className="text-xs text-purple-800">→ Calculado automáticamente</span>
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-purple-700 font-medium text-xs">
                  ✅ Variables validadas con el modelo Policy de la base de datos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Información de tu Agencia</CardTitle>
          <CardDescription>
            Esta información se usará en todas las campañas de email
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="agency_name">Nombre de la Agencia *</Label>
              <Input
                id="agency_name"
                type="text"
                placeholder="Mi Agencia de Seguros"
                value={formData.agency_name}
                onChange={(e) => setFormData({ ...formData, agency_name: e.target.value })}
                required
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se usa en: {'{{nombre_agencia}}'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="agency_phone">Teléfono de Contacto</Label>
                <Input
                  id="agency_phone"
                  type="tel"
                  placeholder="555-123-4567"
                  value={formData.agency_phone}
                  onChange={(e) => setFormData({ ...formData, agency_phone: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se usa en: {'{{telefono_agencia}}'}
                </p>
              </div>

              <div>
                <Label htmlFor="agency_email">Email de Contacto</Label>
                <Input
                  id="agency_email"
                  type="email"
                  placeholder="contacto@miagencia.com"
                  value={formData.agency_email}
                  onChange={(e) => setFormData({ ...formData, agency_email: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se usa en: {'{{email_agencia}}'}
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="agency_website">Sitio Web</Label>
              <Input
                id="agency_website"
                type="url"
                placeholder="https://www.miagencia.com"
                value={formData.agency_website}
                onChange={(e) => setFormData({ ...formData, agency_website: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se usa en: {'{{sitio_web_agencia}}'}
              </p>
            </div>

            <div>
              <Label htmlFor="agency_address">Dirección Física</Label>
              <Input
                id="agency_address"
                type="text"
                placeholder="Av. Principal #123, Ciudad"
                value={formData.agency_address}
                onChange={(e) => setFormData({ ...formData, agency_address: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="signature_text">Firma Personalizada (Opcional)</Label>
              <Textarea
                id="signature_text"
                placeholder="Atentamente,&#10;El equipo de..."
                value={formData.signature_text}
                onChange={(e) => setFormData({ ...formData, signature_text: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="footer_text">Texto del Footer (Opcional)</Label>
              <Textarea
                id="footer_text"
                placeholder="© 2024 Mi Agencia. Todos los derechos reservados."
                value={formData.footer_text}
                onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Guardar Configuración
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/email-campaigns')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
